import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useText } from "@/lib/useText";
import { useContent } from "@/context/ContentContext";

const hour = (t) => new Date(t * 1000).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });

function Stat({ label, value, testId }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-widest text-eden-muted">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold tabular-nums text-eden-text" data-testid={testId}>{value}</p>
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-sm border border-eden-border bg-eden-bg px-3 py-2 text-xs text-eden-text shadow-lg">
      <p className="font-mono text-eden-muted">{hour(p.t)}</p>
      <p className="mt-0.5 font-semibold">{p.online ? `${p.players} jogadores` : "Servidor offline"}</p>
    </div>
  );
}

export function PlayerHistory() {
  const [data, setData] = useState(null);
  const t = useText();
  const { content } = useContent();
  const accent = content?.theme?.orange || "#FF5500";

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/server/history").then(({ data: d }) => alive && setData(d)).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!data || data.points.length < 2) return null;
  const now = data.points[data.points.length - 1];

  return (
    <section data-reveal className="border-b border-eden-border py-14 sm:py-20" aria-labelledby="history-title" data-testid="player-history-section">
      <div className="container-eden grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-3">
          <p className="eyebrow mb-4">{t("history_eyebrow")}</p>
          <h2 id="history-title" className="text-2xl font-bold tracking-tight text-eden-text sm:text-3xl">{t("history_title")}</h2>
          <div className="mt-8 grid grid-cols-3 gap-4 lg:grid-cols-1 lg:gap-6">
            <Stat label={t("history_now")} value={now.online ? now.players : 0} testId="history-now" />
            <Stat label={t("history_peak")} value={data.peak} testId="history-peak" />
            <Stat label={t("history_avg")} value={data.average} testId="history-average" />
          </div>
        </div>
        <div className="h-64 lg:col-span-9 lg:h-72" data-testid="player-history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.points} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="playersFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" tickFormatter={hour} stroke="#3a3a42" tick={{ fill: "#8a8a94", fontSize: 11, fontFamily: "monospace" }} tickLine={false} axisLine={false} minTickGap={48} />
              <YAxis allowDecimals={false} stroke="#3a3a42" tick={{ fill: "#8a8a94", fontSize: 11, fontFamily: "monospace" }} tickLine={false} axisLine={false} domain={[0, data.max_players || "auto"]} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: accent, strokeOpacity: 0.4 }} />
              <Area type="monotone" dataKey="players" stroke={accent} strokeWidth={2} fill="url(#playersFill)" isAnimationActive activeDot={{ r: 4, fill: accent, stroke: "#0b0b0d" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
